import React, { useState, useEffect } from "react";
import { Input } from "@showitinc/showit-wc";
import { Map } from "immutable";
import AppConstants from "../../constants/AppConstants";
import { initFormatter } from "../../utils/PropertyFormatters";
import LottiePlaybackControls from "./LottiePlaybackControls";
import LottieStore from "../../stores/LottieStore";
import useStoreState from "../providers/useStoreState";
import { validateLottieHostUrl } from "../../utils/LottieUtils";
import styles from "./LottieSelectInput.module.css";

const { UPDATE_ACTION_SET, LOTTIE_TYPE_ASSET_LINK } = AppConstants;

const ERROR_MESSAGES = {
  protocol: "URL must use HTTPS",
  domain: "URL must be from lottie.host domain",
  invalid: "Invalid URL format",
};

/**
 * Settings panel for Lottie elements using external URL links.
 * Provides URL input with lottie.host validation and playback controls.
 *
 * @param {Object} props
 * @param {Map} props.element - The Lottie element data
 * @param {string} props.elementId - The element ID for store lookups
 * @param {boolean} props.isLocked - Whether the element is locked
 * @param {string} props.layout - The current layout (desktop/mobile)
 * @param {Function} props.onPropChange - Callback for property changes
 */
const LottieAssetLinkSettings = ({ element, elementId, isLocked, layout, onPropChange }) => {
  const hasLoadError = useStoreState(LottieStore, () =>
    LottieStore.hasLoadError(elementId, layout),
  );

  const lottie = element.get("content", Map());
  const savedUrl = lottie.get("url", "");
  const [inputUrl, setInputUrl] = useState(savedUrl);

  useEffect(() => {
    setInputUrl(savedUrl);
  }, [savedUrl]);

  const _onUrlChange = (propInfo) => {
    const newUrl = propInfo.value ?? "";
    setInputUrl(newUrl);

    const trimmed = newUrl.trim();
    if (trimmed && !validateLottieHostUrl(trimmed).isValid) return;

    let content = lottie.set("type", LOTTIE_TYPE_ASSET_LINK);
    content = trimmed ? content.set("url", trimmed) : content.delete("url");

    onPropChange({
      ...propInfo,
      value: content.toJS(),
      action: UPDATE_ACTION_SET,
    });
  };

  const separatePath = (path) => path.split(".");
  const getValue = (path, fallback) => element.getIn(separatePath(path), fallback);
  const prepChange = (path, defaultValue) => [path, getValue(path), true, false, defaultValue];

  const { onChange: onUrlChange } = initFormatter(_onUrlChange);

  const validation = validateLottieHostUrl(inputUrl);

  const getErrorMessage = () => {
    if (inputUrl && !validation.isValid) {
      return ERROR_MESSAGES[validation.errorType] || "Invalid URL";
    }

    if (inputUrl && validation.isValid && hasLoadError) {
      return "Unable to load animation from URL";
    }

    return null;
  };

  const errorMessage = getErrorMessage();

  return (
    <div>
      <Input
        data-testid="lottie-url-input"
        disabled={isLocked}
        label="Asset Link"
        type="url"
        value={inputUrl}
        onSIBeforeChange={onUrlChange(...prepChange("content"))}
        onSIInput={onUrlChange(...prepChange("content"))}
        placeholder="https://lottie.host/..."
        invalid={Boolean(errorMessage)}
        longLabel
      />
      {errorMessage && <p className={styles.errorText}>{errorMessage}</p>}
      <p className={`instrux ${styles.instrux}`}>
        Enter a Lottie file URL from lottie.host (e.g., https://lottie.host/.../file.lottie).{" "}
        <a href="https://learn.showit.com/en/articles/13754801-lottie-animations" target="blank">
          Learn more about Lottie files.
        </a>
      </p>
      <LottiePlaybackControls element={element} isLocked={isLocked} onPropChange={onPropChange} />
    </div>
  );
};

export default LottieAssetLinkSettings;
